import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, decimals, Empty, Field, Note, Panel, Spinner, TableWrap } from '@wyocspay/ui';
import {
  ApiError,
  api,
  type ReaderRow,
  type ReadersPage,
  type ReaderPaymentProgress,
  type TerminalRow,
  type TerminalsPage,
} from './api.js';
import { dateTime, money, relative } from './format.js';

/**
 * Taking payments in person, which this platform offers two ways of doing.
 *
 * They are not two settings on one thing, they are two different purchases with
 * different money attached, so the page asks which before it shows a form:
 *
 *   A phone running WyoCSPayPos. No hardware to buy at all - the merchant uses
 *   a handset they already own, provided it meets Apple's and Google's terms,
 *   which are listed here because a business that learns them after buying
 *   phones has been failed by our silence. The phone is the till: it shows the
 *   total, reads the card, and is operated by whoever is serving.
 *
 *   A Stripe card reader. Hardware bought from Stripe, and the opposite
 *   arrangement in every respect: no app, no phone, nothing of ours running on
 *   the device. The sale is driven from this panel - somebody types the amount
 *   here and the reader on the counter wakes up - which is why the reader
 *   section below has a keypad in it and the phone section does not.
 *
 * Both paths end in the same ledger. A payment taken on either appears in
 * Payments, in the reports and in the audit trail exactly as a payment link
 * does, and carries the same fee.
 *
 * Three things have to be true before either form is worth showing, and all
 * three are answered by the server rather than guessed here: the platform has
 * granted this business in-person acceptance, and - separately per path - the
 * card schemes allow Tap to Pay in its country, and Stripe ships reader
 * hardware there. The two country answers genuinely differ, so they are asked
 * separately; a merchant in Mexico can tap on an iPhone and cannot buy a reader.
 */

type Path = 'phone' | 'reader';

function deviceLine(t: TerminalRow): string {
  const parts = [t.device_model, t.platform ? t.platform.toUpperCase() : null, t.os_version]
    .filter(Boolean)
    .join(' · ');
  return parts || 'Unknown device';
}

/**
 * `stripe_s700` reads like a database value because it is one. The panel is
 * read by shopkeepers, so the model is spelled the way it is on the box.
 */
const READER_MODELS: Record<string, string> = {
  bbpos_wisepos_e: 'BBPOS WisePOS E',
  stripe_s700: 'Stripe Reader S700',
  stripe_s710: 'Stripe Reader S710',
  stripe_m2: 'Stripe Reader M2',
  verifone_P400: 'Verifone P400',
  verifone_v660p: 'Verifone V660p',
  verifone_ux700: 'Verifone UX700',
  verifone_p630: 'Verifone P630',
  verifone_m425: 'Verifone M425',
  simulated_wisepos_e: 'Simulated WisePOS E',
  simulated_stripe_s700: 'Simulated Reader S700',
  simulated_stripe_s710: 'Simulated Reader S710',
};

function modelName(deviceType: string | null): string {
  if (!deviceType) return 'Card reader';
  return READER_MODELS[deviceType] ?? deviceType.replace(/_/g, ' ');
}

type Message = { tone: 'ok' | 'warn' | 'danger'; text: string } | null;

function messageFrom(err: unknown): Message {
  return { tone: 'danger', text: err instanceof ApiError ? err.message : String(err) };
}

export function TerminalsView({ onNavigate }: { onNavigate?: (view: string) => void }) {
  const [phones, setPhones] = useState<TerminalsPage | null>(null);
  const [readers, setReaders] = useState<ReadersPage | null>(null);
  const [path, setPath] = useState<Path | null>(null);
  const [msg, setMsg] = useState<Message>(null);

  const loadPhones = useCallback(async () => {
    try {
      setPhones(await api.terminals());
    } catch (err) {
      setMsg(messageFrom(err));
    }
  }, []);

  const loadReaders = useCallback(async () => {
    try {
      setReaders(await api.readers());
    } catch (err) {
      setMsg(messageFrom(err));
    }
  }, []);

  useEffect(() => {
    void loadPhones();
    void loadReaders();
  }, [loadPhones, loadReaders]);

  /**
   * A business that has already chosen is not asked again.
   *
   * The choice below is worth making once, when somebody is deciding what to
   * buy. Making a shopkeeper who opens this page every morning to check their
   * tills pick "phone" again first is the sort of politeness that turns into an
   * extra click a thousand times. So: devices of exactly one kind selects that
   * kind, and anything else - none, or both - leaves the choice showing.
   */
  useEffect(() => {
    if (path !== null || !phones || !readers) return;
    const hasPhones = phones.data.length > 0;
    const hasReaders = readers.data.length > 0;
    if (hasPhones && !hasReaders) setPath('phone');
    else if (hasReaders && !hasPhones) setPath('reader');
  }, [path, phones, readers]);

  if (!phones || !readers) {
    return msg ? <Note tone={msg.tone}>{msg.text}</Note> : <Spinner label="Loading…" />;
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Chooser
        path={path}
        onChoose={(next) => {
          setPath(next);
          setMsg(null);
        }}
        phones={phones}
        readers={readers}
      />

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      {path === 'phone' ? (
        <PhonePath page={phones} reload={loadPhones} onNavigate={onNavigate} setMsg={setMsg} />
      ) : null}
      {path === 'reader' ? (
        <ReaderPath page={readers} reload={loadReaders} onNavigate={onNavigate} setMsg={setMsg} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The choice
// ---------------------------------------------------------------------------

/**
 * The two options, side by side and permanently on the page.
 *
 * Not a wizard step that disappears once answered. A merchant who has phones
 * and is wondering whether to buy a reader should be able to read what a reader
 * is without unregistering anything, and one running both needs to move between
 * the two lists all day.
 *
 * Each card says the thing that actually decides it - whether there is hardware
 * to buy, and where the sale is rung up - rather than a feature list. Those are
 * the two facts a shopkeeper is weighing.
 */
function Chooser({
  path,
  onChoose,
  phones,
  readers,
}: {
  path: Path | null;
  onChoose: (path: Path) => void;
  phones: TerminalsPage;
  readers: ReadersPage;
}) {
  const phoneCount = phones.data.filter((t) => t.status === 'ACTIVE').length;
  const readerCount = readers.data.filter((r) => r.status === 'ACTIVE').length;

  return (
    <Panel title="How do you want to take payments in person?">
      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}
      >
        <Choice
          selected={path === 'phone'}
          onSelect={() => onChoose('phone')}
          title="WyoCSPayPos on a phone"
          summary="No hardware to buy. Install the app on a phone you already own and tap cards against it."
          points={[
            'Nothing to order and nothing to wait for',
            'The phone shows the total and reads the card',
            'The handset has to meet Apple’s or Google’s terms',
          ]}
          count={phoneCount}
          unavailable={phones.tap_to_pay.unavailable}
          unavailableNote={`Not available in ${phones.tap_to_pay.country}`}
        />
        <Choice
          selected={path === 'reader'}
          onSelect={() => onChoose('reader')}
          title="A Stripe card reader"
          summary="Hardware you buy from Stripe. No phone and no app - the sale is rung up here, in this panel."
          points={[
            'A machine on the counter, bought from Stripe',
            'You type the amount here; the reader wakes up',
            'Chip, contactless and PIN, on a purpose-built device',
          ]}
          count={readerCount}
          unavailable={!readers.readers.available}
          unavailableNote={`Not sold in ${readers.readers.country}`}
        />
      </div>
    </Panel>
  );
}

function Choice({
  selected,
  onSelect,
  title,
  summary,
  points,
  count,
  unavailable,
  unavailableNote,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  summary: string;
  points: string[];
  count: number;
  unavailable: boolean;
  unavailableNote: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        textAlign: 'left',
        cursor: 'pointer',
        display: 'grid',
        gap: 8,
        padding: 14,
        borderRadius: 10,
        background: selected ? 'var(--wc-surface-2, rgba(255,255,255,0.03))' : 'transparent',
        border: `1px solid ${selected ? 'var(--wc-accent, #4c8dff)' : 'var(--wc-border, rgba(255,255,255,0.12))'}`,
        /**
         * Unavailable is dimmed, never removed. A shop deciding what to buy has
         * to be able to see that the other way exists and does not work here -
         * an option that is simply absent looks like an option we forgot.
         */
        opacity: unavailable ? 0.55 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>{title}</strong>
        {count > 0 ? <Badge tone="ok">{count} active</Badge> : null}
        {unavailable ? <Badge tone="neutral">{unavailableNote}</Badge> : null}
      </div>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--wc-muted)', lineHeight: 1.55 }}>{summary}</p>
      <ul
        style={{
          margin: 0,
          paddingLeft: 18,
          fontSize: 12.5,
          color: 'var(--wc-faint)',
          lineHeight: 1.6,
        }}
      >
        {points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </button>
  );
}

/**
 * The capability notice, shared by both paths because the grant is shared.
 *
 * One approval covers in-person acceptance however the card is read, so a
 * merchant who has been refused for phones has been refused for readers too and
 * telling them twice in two different sentences would be a lie by arrangement.
 */
function CapabilityNote({
  feature,
  onNavigate,
}: {
  feature: TerminalsPage['feature'];
  onNavigate?: (view: string) => void;
}) {
  return (
    <Note tone="warn" title="Card terminals are not enabled for your account yet">
      <p style={{ margin: 0 }}>
        Taking cards in person is granted per business rather than switched on for everyone. It
        changes which PCI questionnaire you answer and puts a payment device in the hands of people
        who never signed in to anything, so the platform team reads each request before enabling it.
        One approval covers both a phone and a reader.
      </p>
      {feature.blocked_reason ? (
        <p style={{ margin: '8px 0 0', color: 'var(--wc-orange)' }}>{feature.blocked_reason}</p>
      ) : null}
      {feature.requestable && onNavigate ? (
        <div style={{ marginTop: 10 }}>
          <button type="button" className="wc-btn wc-btn-sm" onClick={() => onNavigate('features')}>
            Request card terminals
          </button>
        </div>
      ) : feature.requestable ? (
        <p style={{ margin: '8px 0 0' }}>
          Ask for it under <strong>Capabilities</strong>.
        </p>
      ) : null}
    </Note>
  );
}

// ---------------------------------------------------------------------------
// Phones
// ---------------------------------------------------------------------------

/**
 * Unchanged in every respect that a merchant can see.
 *
 * Registration is one direction and one direction only: the phone shows a code,
 * somebody signed in here types it. There is no invitation to email, no password
 * to set on the device and no way to enrol a terminal from the phone alone -
 * which is the point rather than a simplification. A card terminal is a shared
 * object; merchant credentials typed into one would put an account that can
 * refund, read the ledger and change bank details onto a device several people
 * hold, and would leave it there.
 */
function PhonePath({
  page,
  reload,
  onNavigate,
  setMsg,
}: {
  page: TerminalsPage;
  reload: () => Promise<void>;
  onNavigate?: (view: string) => void;
  setMsg: (msg: Message) => void;
}) {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);

  const { data: rows, feature, tap_to_pay: availability } = page;
  const live = rows.filter((t) => t.status === 'ACTIVE');
  /**
   * The country is checked before the grant, because it is the answer that
   * cannot change. Telling a business in an unsupported country to ask the
   * platform team for permission would send them to be refused by somebody who
   * has no power to say yes.
   */
  const canRegister = !availability.unavailable && feature.granted;

  async function claim(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.claimTerminal({ code: code.trim(), label: label.trim() });
      setMsg({
        tone: 'ok',
        text: `Registered as "${r.label}". The phone will finish on its own within a few seconds - there is nothing more to press there.`,
      });
      setCode('');
      setLabel('');
      await reload();
    } catch (err) {
      setMsg(messageFrom(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      await api.removeTerminal(id, 'Removed from the merchant panel');
      setConfirming(null);
      setMsg({
        tone: 'warn',
        text: 'Removed. That phone stops taking payments at its next request, and can be paired again from the app.',
      });
      await reload();
    } catch (err) {
      setMsg(messageFrom(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Turning a phone into a card terminal">
        Install <strong>WyoCSPayPos</strong> on the phone and open it. It shows a six-character code
        that lasts fifteen minutes. Type that code below and the phone becomes a terminal for this
        business - it takes contactless cards, Apple Pay and Google Pay by tapping them against the
        handset. No card reader, and no card number is ever typed into anything.
      </Note>

      {/*
        The handset requirements, said before a phone is bought rather than
        after.

        These are not ours and we cannot waive any of them: Apple and Google
        both refuse to let a device accept a card unless it attests to its own
        integrity first. What that means in a shop is that a perfectly good
        second-hand Android with an unlocked bootloader, or a phone somebody
        rooted years ago, simply will not work - and the app cannot say why in
        any useful detail, because the refusal happens below it.

        A business that learns this after buying handsets blames the platform,
        and it would be right to: we knew and did not say.
      */}
      <Panel title="What the phone has to be">
        <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          <div>
            <strong style={{ fontSize: 13.5 }}>iPhone</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--wc-muted)', lineHeight: 1.6 }}>
              <li>iPhone XS or newer</li>
              <li>A current iOS release, not a beta</li>
              <li>No card reader and no accessory of any kind</li>
            </ul>
          </div>
          <div>
            <strong style={{ fontSize: 13.5 }}>Android</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--wc-muted)', lineHeight: 1.6 }}>
              <li>Android 13 or newer, with NFC</li>
              <li>Google Play services, on a Google-certified handset</li>
              <li>Not rooted, bootloader untouched, developer options off</li>
              <li>A security update from the last twelve months</li>
            </ul>
          </div>
        </div>
        <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.55 }}>
          These are set by Apple and Google, not by us, and there is no setting here that can lift
          them. The phone checks itself before it will accept a card, so a handset that fails one of
          them will pair and then refuse at the counter. Worth checking before buying.
        </p>
      </Panel>

      {availability.unavailable ? (
        <Note tone="warn" title={`Tap to Pay is not available in ${availability.country}`}>
          <p style={{ margin: 0 }}>
            Neither iPhone nor Android can accept cards this way for a business registered in{' '}
            {availability.country}. That is decided by the card schemes and by Stripe, not by us,
            and there is no setting here or approval we could give that would change it. If your
            business moves to a country where it is offered, this page will offer it too.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--wc-faint)' }}>
            Availability last checked {availability.listsCheckedOn}.
          </p>
        </Note>
      ) : !feature.granted ? (
        <CapabilityNote feature={feature} onNavigate={onNavigate} />
      ) : availability.ios !== availability.android ? (
        /**
         * Only one of the two platforms works here, and which one decides what
         * hardware the business has to buy. Said before they pair rather than
         * after, because a shop that has bought the wrong handset has spent
         * money on our silence.
         */
        <Note title={`Only ${availability.ios ? 'iPhone' : 'Android'} works in ${availability.country}`}>
          Tap to Pay is available in {availability.country} on{' '}
          <strong>{availability.ios ? 'iPhone' : 'Android'}</strong> only. The app will still show a
          code on {availability.ios ? 'an Android handset' : 'an iPhone'}, and the payment will still
          fail at the card, so use {availability.ios ? 'iPhones' : 'Android phones'} for the counter.
          Availability last checked {availability.listsCheckedOn}.
        </Note>
      ) : null}

      {canRegister ? (
        <Panel title="Register a phone">
          {/*
            Three controls on one line, which is harder than it looks.

            `align-items: flex-end` aligns the bottom of each child, and the two
            fields were not the same shape: one carried a hint underneath, so its
            bottom edge was the bottom of the hint rather than of the input, and
            the code box was taller again because its type is larger. The row
            settled on three different baselines and read as broken.

            Both fields are now label-and-input and nothing else, the two inputs
            are pinned to the same height so the larger type cannot grow one of
            them, and the hint has moved into the paragraph below where it can
            change length without moving anything.
          */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '0 0 190px' }}>
              <Field label="Code from the phone">
                <input
                  className="wc-input wc-num"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="42JC9D"
                  maxLength={12}
                  autoCapitalize="characters"
                  spellCheck={false}
                  style={{
                    fontSize: 20,
                    letterSpacing: 3,
                    textAlign: 'center',
                    height: 44,
                    boxSizing: 'border-box',
                  }}
                />
              </Field>
            </div>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <Field label="Name it">
                <input
                  className="wc-input"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Front counter"
                  maxLength={60}
                  style={{ height: 44, boxSizing: 'border-box' }}
                />
              </Field>
            </div>
            <button
              type="button"
              className="wc-btn"
              onClick={() => void claim()}
              disabled={busy || code.trim().length < 4 || !label.trim()}
              style={{ height: 44, flex: 'none' }}
            >
              {busy ? 'Registering…' : 'Register'}
            </button>
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.55 }}>
            Name it after where the phone lives - “Front counter”, “Van 2” - so you can tell one from
            another later. Codes are single-use and expire after fifteen minutes; if one does not
            work, the phone will show a fresh one.
          </p>
        </Panel>
      ) : null}

      <Panel title={`Phones${live.length > 0 ? ` · ${live.length} active` : ''}`} padded={false}>
        {rows.length === 0 ? (
          <Empty>No phone has been registered yet.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Device</th>
                  <th>Registered</th>
                  <th>Last seen</th>
                  <th>Last problem</th>
                  <th>State</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const open = confirming === t.id;
                  const active = t.status === 'ACTIVE';
                  return (
                    <tr key={t.id} style={{ opacity: active ? 1 : 0.6 }}>
                      <td style={{ fontWeight: 600, fontSize: 13.5 }}>{t.label}</td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{deviceLine(t)}</td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)' }} title={dateTime(t.registered_at)}>
                        {relative(t.registered_at)}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>
                        {t.last_seen_at ? relative(t.last_seen_at) : 'never'}
                      </td>
                      {/*
                        What the operator was actually shown, in the merchant's
                        own list. A phone that will not take cards is reported by
                        telephone as "it doesn't work", which is the same
                        sentence for an amount below Stripe's floor, developer
                        options left on, and a card lifted too early. This column
                        is the difference between those.
                      */}
                      <td style={{ fontSize: 12.5, maxWidth: 260 }}>
                        {t.last_problem ? (
                          <>
                            <div
                              style={{
                                color: t.last_problem.permanent ? 'var(--wc-danger)' : 'var(--wc-muted)',
                                fontWeight: t.last_problem.permanent ? 600 : 400,
                              }}
                            >
                              {t.last_problem.message}
                            </div>
                            <div
                              style={{ fontSize: 11, color: 'var(--wc-faint)', marginTop: 2 }}
                              title={dateTime(t.last_problem.at)}
                            >
                              {relative(t.last_problem.at)}
                              {t.last_problem.permanent ? ' - needs fixing on the phone' : null}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--wc-faint)' }}>none</span>
                        )}
                      </td>
                      <td>
                        {/*
                          Three states, not two. A freshly paired phone is
                          PENDING_REVIEW - it is neither working nor removed, and
                          calling it "REMOVED" would tell a merchant their setup
                          failed when it is simply waiting for WyoCSPay to
                          approve the business. It gets its own amber badge and a
                          line saying what happens next.
                        */}
                        {t.status === 'PENDING_REVIEW' ? (
                          <Badge tone="pending">AWAITING APPROVAL</Badge>
                        ) : t.status === 'REJECTED' ? (
                          <Badge tone="fail">NOT APPROVED</Badge>
                        ) : (
                          <Badge tone={active ? 'ok' : 'neutral'}>{active ? 'ACTIVE' : 'REMOVED'}</Badge>
                        )}
                        {t.status === 'PENDING_REVIEW' ? (
                          <div style={{ fontSize: 11.5, color: 'var(--wc-muted)', marginTop: 2 }}>
                            Waiting for WyoCSPay to approve this device. It cannot take payments yet.
                          </div>
                        ) : null}
                        {/*
                          The reason, because a terminal can also be removed by
                          the platform. A business whose till stopped working
                          should be able to read why here rather than ring up to
                          ask whether they did it themselves.
                        */}
                        {t.status !== 'PENDING_REVIEW' && !active && t.revoked_reason ? (
                          <div style={{ fontSize: 11.5, color: 'var(--wc-muted)', marginTop: 2 }}>
                            {t.revoked_reason}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {!active ? (
                          <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
                            {t.revoked_at ? relative(t.revoked_at) : 'removed'}
                          </span>
                        ) : open ? (
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-danger"
                              onClick={() => void remove(t.id)}
                              disabled={busy}
                            >
                              Remove it
                            </button>
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-secondary"
                              onClick={() => setConfirming(null)}
                              disabled={busy}
                            >
                              Keep
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="wc-btn wc-btn-sm wc-btn-secondary"
                            onClick={() => setConfirming(t.id)}
                            disabled={busy}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
        <p style={{ padding: '10px 16px', margin: 0, fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.55 }}>
          Removing a phone stops it taking payments at its very next request. Payments it already
          took are unaffected and stay in your ledger. A phone can hold one terminal at a time, so a
          handset removed here is free to be registered again - here or by another business.
        </p>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stripe readers
// ---------------------------------------------------------------------------

function ReaderPath({
  page,
  reload,
  onNavigate,
  setMsg,
}: {
  page: ReadersPage;
  reload: () => Promise<void>;
  onNavigate?: (view: string) => void;
  setMsg: (msg: Message) => void;
}) {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; label: string } | null>(null);
  const [charging, setCharging] = useState<ReaderRow | null>(null);

  const { data: rows, feature, readers: availability } = page;
  const live = rows.filter((r) => r.status === 'ACTIVE');
  const canRegister = availability.available && feature.granted;

  async function register(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const reader = await api.registerReader({
        registration_code: code.trim(),
        label: label.trim(),
      });
      setMsg({
        tone: 'ok',
        text: `Registered "${reader.label}". The reader will show your business name on its own screen within a few seconds.`,
      });
      setCode('');
      setLabel('');
      await reload();
    } catch (err) {
      setMsg(messageFrom(err));
    } finally {
      setBusy(false);
    }
  }

  async function rename(): Promise<void> {
    if (!renaming) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.renameReader(renaming.id, renaming.label.trim());
      setRenaming(null);
      await reload();
    } catch (err) {
      setMsg(messageFrom(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      await api.removeReader(id, 'Removed from the merchant panel');
      setConfirming(null);
      setMsg({
        tone: 'warn',
        text: 'Removed. The reader is deregistered at Stripe and will not take another payment for this business until it is registered again.',
      });
      await reload();
    } catch (err) {
      setMsg(messageFrom(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Registering a Stripe reader">
        On the reader itself, open <strong>Settings</strong> and choose{' '}
        <strong>Generate pairing code</strong>. It shows three words joined by hyphens. Type them
        below and the machine becomes a till for this business. There is nothing to install on it
        and nothing to sign in to - the sale is rung up here and Stripe wakes the reader.
      </Note>

      {!availability.available ? (
        <Note tone="warn" title={`Stripe does not sell readers in ${availability.country}`}>
          <p style={{ margin: 0 }}>
            Stripe does not ship or activate reader hardware for a business registered in{' '}
            {availability.country}. That is Stripe's decision about where its hardware works, not
            ours, and no approval here would change it. A phone running WyoCSPayPos may still be an
            option - see the other choice above.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--wc-faint)' }}>
            Availability last checked {availability.listsCheckedOn}.
          </p>
        </Note>
      ) : !feature.granted ? (
        <CapabilityNote feature={feature} onNavigate={onNavigate} />
      ) : null}

      {canRegister ? (
        <Panel title="Register a reader">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '0 0 260px' }}>
              <Field label="Pairing code from the reader">
                <input
                  className="wc-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toLowerCase())}
                  placeholder="puce-fondly-tremble"
                  maxLength={80}
                  autoCapitalize="none"
                  spellCheck={false}
                  style={{ height: 44, boxSizing: 'border-box' }}
                />
              </Field>
            </div>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <Field label="Name it">
                <input
                  className="wc-input"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Front counter"
                  maxLength={60}
                  style={{ height: 44, boxSizing: 'border-box' }}
                />
              </Field>
            </div>
            <button
              type="button"
              className="wc-btn"
              onClick={() => void register()}
              disabled={busy || code.trim().length < 4 || !label.trim()}
              style={{ height: 44, flex: 'none' }}
            >
              {busy ? 'Registering…' : 'Register'}
            </button>
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.55 }}>
            The name goes to the reader too, so whatever you type here is what the machine calls
            itself. Pairing codes expire - if one is refused, generate a fresh one on the device.
          </p>
        </Panel>
      ) : null}

      {charging ? (
        <ChargePanel
          reader={charging}
          currency={page.currency}
          onClose={() => setCharging(null)}
          setMsg={setMsg}
        />
      ) : null}

      <Panel title={`Readers${live.length > 0 ? ` · ${live.length} active` : ''}`} padded={false}>
        {rows.length === 0 ? (
          <Empty>No reader has been registered yet.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Model</th>
                  <th>Registered</th>
                  <th>Last seen</th>
                  <th>State</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const active = r.status === 'ACTIVE';
                  const isRenaming = renaming?.id === r.id;
                  return (
                    <tr key={r.id} style={{ opacity: active ? 1 : 0.6 }}>
                      <td style={{ fontWeight: 600, fontSize: 13.5 }}>
                        {isRenaming ? (
                          <input
                            className="wc-input"
                            value={renaming.label}
                            onChange={(e) => setRenaming({ id: r.id, label: e.target.value })}
                            maxLength={60}
                            style={{ height: 30, boxSizing: 'border-box', fontSize: 13 }}
                          />
                        ) : (
                          r.label
                        )}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>
                        {modelName(r.device_type)}
                        {r.serial_number ? (
                          <div style={{ fontSize: 11.5, color: 'var(--wc-faint)', marginTop: 2 }}>
                            {r.serial_number}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)' }} title={dateTime(r.registered_at)}>
                        {relative(r.registered_at)}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>
                        {r.last_seen_at ? relative(r.last_seen_at) : 'never'}
                      </td>
                      <td>
                        {active ? (
                          <Badge tone={r.connection_status === 'online' ? 'ok' : 'warn'}>
                            {r.connection_status === 'online' ? 'ONLINE' : 'OFFLINE'}
                          </Badge>
                        ) : (
                          <Badge tone="neutral">REMOVED</Badge>
                        )}
                        {!active && r.removed_reason ? (
                          <div style={{ fontSize: 11.5, color: 'var(--wc-muted)', marginTop: 2 }}>
                            {r.removed_reason}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {!active ? (
                          <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
                            {r.removed_at ? relative(r.removed_at) : 'removed'}
                          </span>
                        ) : isRenaming ? (
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm"
                              onClick={() => void rename()}
                              disabled={busy || !renaming.label.trim()}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-secondary"
                              onClick={() => setRenaming(null)}
                              disabled={busy}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : confirming === r.id ? (
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-danger"
                              onClick={() => void remove(r.id)}
                              disabled={busy}
                            >
                              Remove it
                            </button>
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-secondary"
                              onClick={() => setConfirming(null)}
                              disabled={busy}
                            >
                              Keep
                            </button>
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            {feature.granted ? (
                              <button
                                type="button"
                                className="wc-btn wc-btn-sm"
                                onClick={() => setCharging(r)}
                                disabled={busy}
                              >
                                Take a payment
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-secondary"
                              onClick={() => setRenaming({ id: r.id, label: r.label })}
                              disabled={busy}
                            >
                              Rename
                            </button>
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-secondary"
                              onClick={() => setConfirming(r.id)}
                              disabled={busy}
                            >
                              Remove
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
        <p style={{ padding: '10px 16px', margin: 0, fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.55 }}>
          Removing a reader deregisters it at Stripe, so it stops being able to take a payment for
          this business at all. Payments it already took are unaffected and stay in your ledger. The
          hardware itself is untouched and can be registered again from a fresh pairing code - here,
          or by whoever you sell it to.
        </p>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ringing up a sale
// ---------------------------------------------------------------------------

/**
 * The till, which lives here because there is no app to put it in.
 *
 * With a phone the operator types the amount on the phone. With a reader there
 * is nowhere else for the amount to come from - the machine on the counter has
 * no keypad for the shopkeeper, only one for the cardholder's PIN - so this
 * panel is the point of sale and has to behave like one: a big number, one
 * button, and a screen that tells somebody standing at a counter what is
 * happening on a device they are looking at anyway.
 *
 * The payment is created before Stripe is told to wake the reader, so a sale
 * that fails at the machine is still a payment in the ledger with a reason
 * attached rather than a charge that never existed.
 */
function ChargePanel({
  reader,
  currency,
  onClose,
  setMsg,
}: {
  reader: ReaderRow;
  currency: string;
  onClose: () => void;
  setMsg: (msg: Message) => void;
}) {
  const [amount, setAmount] = useState('');
  const [tip, setTip] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ReaderPaymentProgress | null>(null);

  const places = decimals(currency);
  const parsed = Number.parseFloat(amount);
  const minor = Number.isFinite(parsed) ? Math.round(parsed * 10 ** places) : NaN;
  // The gratuity is agreed here, before the reader is woken, so the figure the
  // customer approves on the device is the final one and the amount check on
  // the webhook stays exact. Blank means no tip, which is the common case.
  const tipParsed = Number.parseFloat(tip);
  const tipMinor = tip.trim() === '' ? 0 : Number.isFinite(tipParsed) ? Math.round(tipParsed * 10 ** places) : NaN;
  const tipValid = tip.trim() === '' || (Number.isFinite(tipMinor) && tipMinor >= 0);
  const valid = Number.isFinite(minor) && minor > 0 && tipValid;

  /**
   * One key per sale, generated when the panel opens and again after each one
   * finishes. It is what makes a second press of "Charge" after a reply that
   * never arrived land on the same payment rather than on a second one.
   */
  const idempotencyKey = useRef(crypto.randomUUID());

  /**
   * Polling, and the reason it stops.
   *
   * The server also learns the outcome from Stripe's webhook, so this loop is
   * about the person at the counter rather than about the money - it is what
   * makes the screen say "approved" while they are still holding their card. It
   * runs only while a sale is open and clears itself on every exit path,
   * because a timer left running against a closed panel is a request every two
   * seconds for as long as the tab is open.
   */
  useEffect(() => {
    if (!paymentId) return undefined;
    let cancelled = false;

    const tick = async (): Promise<void> => {
      try {
        const next = await api.readerPaymentProgress(reader.id, paymentId);
        if (cancelled) return;
        setProgress(next);
        if (next.action.status === 'failed' || finished(next)) {
          setBusy(false);
        }
      } catch (err) {
        if (!cancelled) setMsg(messageFrom(err));
      }
    };

    void tick();
    const timer = setInterval(() => void tick(), 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [paymentId, reader.id, setMsg]);

  const done = progress ? finished(progress) : false;
  const failed = progress?.action.status === 'failed';

  async function charge(): Promise<void> {
    setBusy(true);
    setMsg(null);
    setProgress(null);
    try {
      const started = await api.startReaderPayment(reader.id, {
        amount: minor,
        currency,
        reference: reference.trim() || undefined,
        tip: tipMinor > 0 ? tipMinor : undefined,
        idempotency_key: idempotencyKey.current,
      });
      setPaymentId(started.payment_id);
    } catch (err) {
      setMsg(messageFrom(err));
      setBusy(false);
    }
  }

  async function cancel(): Promise<void> {
    setBusy(true);
    try {
      await api.cancelReaderAction(reader.id, paymentId ?? undefined);
      setMsg({ tone: 'warn', text: 'Cancelled. The reader has cleared and the card was not charged.' });
      reset();
    } catch (err) {
      setMsg(messageFrom(err));
    } finally {
      setBusy(false);
    }
  }

  function reset(): void {
    idempotencyKey.current = crypto.randomUUID();
    setPaymentId(null);
    setProgress(null);
    setAmount('');
    setTip('');
    setReference('');
    setBusy(false);
  }

  return (
    <Panel title={`Take a payment · ${reader.label}`}>
      {paymentId === null ? (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '0 0 200px' }}>
              <Field label={`Amount (${currency.toUpperCase()})`}>
                <input
                  className="wc-input wc-num"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  style={{ fontSize: 22, height: 48, boxSizing: 'border-box', textAlign: 'right' }}
                />
              </Field>
            </div>
            <div style={{ flex: '0 0 150px' }}>
              <Field label={`Tip (${currency.toUpperCase()}, optional)`}>
                <input
                  className="wc-input wc-num"
                  value={tip}
                  onChange={(e) => setTip(e.target.value)}
                  placeholder={places === 0 ? '0' : '0.00'}
                  inputMode="decimal"
                  style={{ fontSize: 22, height: 48, boxSizing: 'border-box', textAlign: 'right' }}
                />
              </Field>
            </div>
            <div style={{ flex: '1 1 180px', minWidth: 0 }}>
              <Field label="Your reference (optional)">
                <input
                  className="wc-input"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Order 1042"
                  maxLength={80}
                  style={{ height: 48, boxSizing: 'border-box' }}
                />
              </Field>
            </div>
            <button
              type="button"
              className="wc-btn"
              onClick={() => void charge()}
              disabled={busy || !valid}
              style={{ height: 48, flex: 'none' }}
            >
              {busy ? 'Sending…' : 'Charge'}
            </button>
            <button
              type="button"
              className="wc-btn wc-btn-secondary"
              onClick={onClose}
              disabled={busy}
              style={{ height: 48, flex: 'none' }}
            >
              Close
            </button>
          </div>

          {/*
            The tip is entered here, before the reader is woken, rather than on
            the reader's own screen. That keeps the amount the customer approves
            on the device equal to the amount written to the ledger, so the
            webhook's amount check stays exact on every channel. Agree the tip
            with the customer, type it, and it is part of the one total they tap.
          */}
          <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.55 }}>
            {tipMinor > 0
              ? `The reader will ask for ${money(minor + tipMinor, currency)} in total, including ${money(tipMinor, currency)} tip.`
              : 'Add a tip above if the customer wants to leave one. The reader shows the single total and reads the card once.'}
          </p>
        </>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 28, fontWeight: 600 }}>
            {money(progress?.amount ?? minor, currency)}
            {progress && progress.tip > 0 ? (
              <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--wc-muted)', marginLeft: 10 }}>
                including {money(progress.tip, currency)} tip
              </span>
            ) : null}
          </div>

          <Note tone={failed ? 'danger' : done ? 'ok' : undefined} title={headline(progress)}>
            <p style={{ margin: 0 }}>{explain(progress, reader.label)}</p>
          </Note>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!done && !failed ? (
              <button
                type="button"
                className="wc-btn wc-btn-secondary"
                onClick={() => void cancel()}
                disabled={busy && progress === null}
              >
                Cancel this sale
              </button>
            ) : null}
            {done || failed ? (
              <>
                <button type="button" className="wc-btn" onClick={reset}>
                  Next sale
                </button>
                <button
                  type="button"
                  className="wc-btn wc-btn-secondary"
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                >
                  Close
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </Panel>
  );
}

/**
 * Settled, from this panel's point of view.
 *
 * Read off WyoCSPay's own state rather than off Stripe's intent status, because
 * the ledger is what the merchant will see in every other part of this panel and
 * a screen that says "paid" before the ledger agrees is a screen that has to be
 * explained later.
 */
function finished(progress: ReaderPaymentProgress): boolean {
  return ['SUCCEEDED', 'FAILED', 'CANCELLED', 'PARTIALLY_REFUNDED', 'REFUNDED'].includes(
    progress.state,
  );
}

function headline(progress: ReaderPaymentProgress | null): string {
  if (!progress) return 'Sending to the reader…';
  if (progress.action.status === 'failed') return 'The reader could not take this payment';
  if (progress.state === 'SUCCEEDED') return 'Paid';
  if (progress.state === 'CANCELLED') return 'Cancelled';
  if (progress.state === 'FAILED') return 'Declined';
  if (progress.intent_status === 'requires_capture') return 'Card read - taking the money';
  return 'Waiting for the card';
}

function explain(progress: ReaderPaymentProgress | null, label: string): string {
  if (!progress) return `Waking ${label}.`;
  if (progress.action.failure_message) return progress.action.failure_message;
  if (progress.state === 'SUCCEEDED') {
    return 'The payment is in your ledger and will settle with the rest of your takings.';
  }
  if (progress.state === 'CANCELLED') return 'Nothing was charged.';
  if (progress.state === 'FAILED') {
    return 'The card was refused. Ask the customer for another and start again.';
  }
  return `${label} is showing the total. The customer can tap, insert or swipe their card, or cancel on the device.`;
}
